/**
 * Acquisition Category Chart - M&A Activity by Product Category
 * Follows ProductCategoryChart pattern with consistent styling
 */

import React, { useMemo, useState } from 'react';
import { withChartConfiguration, ChartProps, EnhancedChartProps } from './withChartConfiguration';
import { UnifiedChartFactory } from '../services/UnifiedChartFactory';
import { Download, FileSpreadsheet, Pencil } from 'lucide-react';
import { useServiceData } from '../hooks/useChartData';
import { ChartContainer } from '../utils/ContainerStyles';
import { acquisitionCategoryService, AcquisitionCategoryApiResponse, AcquisitionCategoryData } from '../services/AcquisitionCategoryService';
import { TitleEditor } from './TitleEditor';
import { customTitleService, TitleKey } from '../services/CustomTitleService';
import { ChartMetadata } from './ChartMetadata';


interface AcquisitionCategoryChartProps extends ChartProps {
  timeframe: string;
  weekOffset: number;
  filters?: Record<string, string[] | undefined>;
  metadata?: {
    current_period: { start: string; end: string };
    cache_status?: string;
    response_time_ms?: number;
  };
}

/**
 * Configuration function for chart options
 */
const getAcquisitionCategoryChartOption = (props: AcquisitionCategoryChartProps & EnhancedChartProps) => {
  const { data, timeframe, weekOffset, filters } = props;

  if (!data?.categories?.length) {
    return null;
  }

  const categories = data.categories as AcquisitionCategoryData[];

  // Check for custom title
  const titleKey: TitleKey = {
    chartType: 'acquisition-category',
    timeframe,
    filters
  };
  const customTitle = customTitleService.getCustomTitle(titleKey);

  // Use custom title if exists, otherwise use data title
  const finalTitle = customTitle?.title || data.title;
  const finalSubtitle = customTitle?.subtitle || data.subtitle;

  // Use UnifiedChartFactory for consistent styling
  const option = UnifiedChartFactory.createAcquisitionCategoryChart(
    categories,
    finalTitle,
    finalSubtitle,
    {
      timeframe: timeframe as '1w' | '2w' | '4w' | '12w' | '6m' | 'ytd' | 'ttm' | 'custom',
      weekOffset: weekOffset,
      chartContext: 'default'
    }
  );

  return option;
};

/**
 * Base component that displays the chart
 */
const AcquisitionCategoryChartBase: React.FC<AcquisitionCategoryChartProps & EnhancedChartProps> = React.memo((props) => {
  const { chartRef, data, isLoading, error, downloadChart, refreshChart, timeframe, weekOffset, filters, metadata } = props;

  // Title editor state - ALL HOOKS MUST BE AT THE TOP
  const [editorOpen, setEditorOpen] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(0);

  // Generate title key
  const titleKey: TitleKey = React.useMemo(() => ({
    chartType: 'acquisition-category',
    timeframe,
    filters
  }), [timeframe, filters]);

  // Check for custom title - use useMemo to re-compute when forceRefresh changes
  const customTitle = React.useMemo(() =>
    customTitleService.getCustomTitle(titleKey),
    [titleKey, forceRefresh]
  );

  // Refresh chart when forceRefresh changes (after state update completes)
  React.useEffect(() => {
    if (forceRefresh > 0) {
      refreshChart();
    }
  }, [forceRefresh, refreshChart]);

  // Get chart option for title extraction (must be before conditional returns)
  const topCategory = data?.categories?.[0]?.name;
  const chartOption = React.useMemo(() =>
    getAcquisitionCategoryChartOption({ data, timeframe, weekOffset, filters } as AcquisitionCategoryChartProps & EnhancedChartProps),
    [data, timeframe, weekOffset, filters, forceRefresh]
  );
  const titleOption = Array.isArray(chartOption?.title) ? chartOption.title[0] : chartOption?.title;
  const currentTitle = titleOption?.text || '';
  const currentSubtitle = titleOption?.subtext || '';

  if (isLoading) {
    return (
      <div className={ChartContainer.loading()}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#633FF3]"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={ChartContainer.error()}>
        <div className="text-red-600">
          <p className="font-semibold">Error loading acquisition category data</p>
          <p className="text-sm mt-1">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!data || !data.categories || data.categories.length === 0) {
    return (
      <div className="p-3 h-96 flex items-center justify-center">
        <div className="text-gray-500">No acquisition data available for this period</div>
      </div>
    );
  }

  // Calculate height based on number of categories (should be 10 max)
  // Account for multi-line labels by giving more space per category
  // const categoryCount = Math.min(data?.categories?.length || 0, 10);

  const handleCSVExport = () => {
    if (!data?.categories) return;

    // Generate CSV content
    const headers = ['Category', 'Acquisition Count', 'Companies'];
    const rows = data.categories.map((cat: AcquisitionCategoryData) => [
      cat.name,
      cat.count,
      cat.companies || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `acquisition-categories-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 relative">
        {/* Action buttons - hidden on mobile for less crowding */}
        <div className="hidden sm:flex absolute top-4 right-4 z-10 gap-1.5">
          <button
            onClick={() => setEditorOpen(true)}
            className="p-1.5 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded shadow-sm border border-gray-300 dark:border-gray-600 transition-colors group"
            title="Edit chart title"
          >
            <Pencil className="w-3 h-3 text-gray-600 dark:text-gray-300 group-hover:text-purple-600 dark:group-hover:text-purple-400" />
            {customTitle && (
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-purple-600 rounded-full border border-white dark:border-gray-800" />
            )}
          </button>
          <button
            onClick={handleCSVExport}
            className="chart-button chart-button-secondary text-xs px-2 py-1.5"
            title="Export data as CSV"
            disabled={isLoading}
          >
            <FileSpreadsheet className="w-3 h-3" />
            CSV
          </button>
          <button
            onClick={() => downloadChart('blog')}
            className="chart-button chart-button-primary text-xs px-2 py-1.5"
            title="Download chart as PNG"
            disabled={isLoading}
          >
            <Download className="w-3 h-3" />
            PNG
          </button>
        </div>

        {/* Chart canvas - title is IN the chart, not here */}
        <div
          ref={chartRef}
          className="w-full"
          style={{
            height: '800px', // Increased height for better spacing
            minHeight: '700px',
            maxHeight: '1200px'
          }}
        />

        {/* Chart Metadata */}
        {metadata && (
          <ChartMetadata
            periodStart={metadata.current_period?.start}
            periodEnd={metadata.current_period?.end}
            cacheStatus={metadata.cache_status}
            responseTimeMs={metadata.response_time_ms}
          />
        )}
      </div>

      {/* Title Editor Modal */}
      <TitleEditor
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        currentTitle={currentTitle}
        currentSubtitle={currentSubtitle}
        onSave={(title, subtitle) => {
          customTitleService.setCustomTitle(titleKey, title, subtitle);
          setForceRefresh(prev => prev + 1); // useEffect will call refreshChart()
          setEditorOpen(false);
        }}
        onReset={() => {
          customTitleService.resetCustomTitle(titleKey);
          setForceRefresh(prev => prev + 1); // useEffect will call refreshChart()
          setEditorOpen(false);
        }}
        chartContext={{
          chartType: 'acquisition-category',
          timeframe,
          isMA: true,
          topCategory: topCategory
        }}
      />
    </>
  );
});

AcquisitionCategoryChartBase.displayName = 'AcquisitionCategoryChartBase';

/**
 * Data fetching wrapper component using unified patterns
 */
const AcquisitionCategoryChartWithData: React.FC<{
  timeframe: string;
  weekOffset: number;
  filters?: Record<string, string[] | undefined>;
  metadata?: {
    current_period: { start: string; end: string };
    cache_status?: string;
    response_time_ms?: number;
  };
}> = ({ timeframe, weekOffset, filters, metadata }) => {
  const { data } = useServiceData<AcquisitionCategoryApiResponse>(
    () => acquisitionCategoryService.getAcquisitionCategoryData(timeframe, weekOffset, filters),
    [timeframe, weekOffset, filters],
    {
      validationEnabled: true,
      retryOnError: true,
      maxRetries: 3
    }
  );

  const EnhancedChart = useMemo(
    () => withChartConfiguration(AcquisitionCategoryChartBase, getAcquisitionCategoryChartOption),
    []
  );

  return (
    <EnhancedChart
      data={data}
      timeframe={timeframe}
      weekOffset={weekOffset}
      filters={filters}
      metadata={metadata}
    />
  );
};

export default AcquisitionCategoryChartWithData;